import { isNil } from '@fxts/core';
import { MShopUtilF } from '@modules/MShop/Util/F/Function/module/MShopUtilF';
import { throttleAnimationFrameLevel } from '@modules/Util/F/Function/animation';
import { UtilS } from '@modules/Util/S/Function/module/UtilS';
import { CartCountView } from '@services/MarppleShop/renderApp/components/atoms/Icon/Icons.ts';
import { FROM_MAIN_REGEXP } from '@services/MarppleShop/renderApp/components/cells/Header/libs/contants/regex.ts';
import { HeaderThemeChangedEvent } from '@services/MarppleShop/renderApp/components/cells/Header/libs/helpers/event.ts';
import {
  initHeadroom,
  setForceUnpinned,
} from '@services/MarppleShop/renderApp/components/cells/Header/libs/helpers/headroom.ts';
import {
  BaseData,
  HeaderBaseData,
  HeaderBaseState,
} from '@services/MarppleShop/renderApp/components/cells/Header/libs/types/header';
import { mpsPostMessage } from '@services/MarppleShop/shared/app/util.ts';
import { listenDocumentEvent } from '@services/MarppleShop/shared/util/event.ts';
import { getSharedData } from '@services/MarppleShop/shared/util/rune.ts';
import Headroom from 'headroom.js';
import { $, View } from 'rune-ts';

export abstract class HeaderAbstract<
  D extends BaseData = HeaderBaseData,
  S extends HeaderBaseState = HeaderBaseState,
> extends View<D> {
  private transparent_override?: boolean;
  headroom?: Headroom;
  headroom_element?: HTMLElement;

  protected constructor(
    props: D,
    public state: S,
    public klass: {
      header: string;
      menu: string;
      back_arrow_btn: string;
      center_part: string;
      instant_bg_change: string;
      transparent: string;
    },
  ) {
    super(props, state);
  }

  abstract transparentEnable(): void;
  abstract transparentDisable(): void;
  onTransparent(_instant: boolean) {}

  protected init() {
    const { headroom } = this.state;
    const is_mobile = this.getIsMobile();

    const header_outer_container_element = this.element();
    const header_element = header_outer_container_element.querySelector(
      `.${this.klass.header}`,
    ) as HTMLDivElement;
    this.headroom_element = header_element;

    // 디폹트는 헤드룸
    if (headroom.use) {
      // transparent 인 경우에, headroom 이 스크롤 하자마자 동작하면 bg change 와 headroom 이 동시에 일어나기 때문에 별로임, 그래서 의도적으로 headroom 을 미룸
      this.headroom = initHeadroom(header_element, {
        ...this.state.headroom,
        offset: this.state.headroom.offset
          ? this.state.headroom.offset
          : this.state.transparent
            ? {
                up: 300,
                down: 300,
              }
            : {
                up: 10,
                down: 10,
              },
      });
    }

    this.delegate('click', `.${this.klass.back_arrow_btn}`, () => {
      this.goBack(MShopUtilF.isApp());
    });

    this.delegate('mouseover', `.${this.klass.menu}`, (_e) => {
      this.transparent_override = false;
      this.redrawBgColor(true);

      if (this.headroom) {
        this.headroom.freeze();
      }
    });

    if (!is_mobile) {
      const header_outer_container_element = this.element();
      if (!header_outer_container_element) return;

      header_outer_container_element.addEventListener('mouseleave', () => {
        this.transparent_override = undefined;
        this.redrawBgColor(true);

        if (this.headroom) {
          this.headroom.unfreeze();
        }
      });

      // position fixed 이어서 body 랑 가로 스크롤 js 로 엮어줘야 자연스럽게 동작
      window.addEventListener(
        'scroll',
        throttleAnimationFrameLevel(() => {
          header_element.style.left = -window.scrollX + 'px';
        }) as any,
      );
    }

    if (this.state.transparent) {
      const bg_change_observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry) return;
          this.state.transparent = entry.isIntersecting;
          this.redrawBgColor();
        },
        {
          rootMargin: '0px 0px 0px 0px',
        },
      );

      bg_change_observer.observe(header_outer_container_element);
    }

    listenDocumentEvent('header_back_button_hide', () => {
      this.state.has_back_btn = false;
      this.redraw();
    });

    listenDocumentEvent('header_center_part_update', ({ detail }) => {
      const center_part = $(this.element()).find(`.${this.klass.center_part}`);
      center_part?.setTextContent(detail?.title);
    });
  }

  private legacyHistoryBack() {
    const noBack = () => {
      if (box.sel('store_url'))
        window.location.href = box.sel('store_url') + (box.sel('is_studio') ? '/settings/styles' : '');
      else window.location.href = `/${T.lang}`;
    };

    if (UtilS.isNboxApp()) {
      window.history.back();
    } else {
      if (
        FROM_MAIN_REGEXP[
          (process.env.NODE_ENV as 'staging' | 'production' | 'development') || 'development'
        ].test(document.referrer)
      ) {
        window.history.back();
      } else noBack();
    }
  }

  public goBack(is_app = false) {
    if (is_app) {
      mpsPostMessage({ goBack: true });
    } else {
      this.legacyHistoryBack();
    }
  }

  public redrawCartCount(cart_count: number) {
    this.data.cart_count = cart_count;

    const cartCountView = this.subView(CartCountView);

    if (!this.data.cart_count) {
      cartCountView?.element().remove();
      return this;
    }

    if (cartCountView) {
      cartCountView.setCount(this.data.cart_count).redraw();
    } else {
      this.element()
        .querySelector('.cart-fill-icon')
        ?.append(new CartCountView({ count: this.data.cart_count }).render());
    }

    return this;
  }

  public redrawBgColor(instant = false) {
    const transparent_value = this.transparent_override ?? this.state.transparent;

    const header_element = this.element().querySelector(`.${this.klass.header}`) as HTMLDivElement;

    this.dispatchEvent(HeaderThemeChangedEvent, {
      bubbles: true,
      detail: {
        instant,
        transparent: transparent_value,
      },
    });

    if (instant) {
      header_element.classList.add(this.klass.instant_bg_change);
    }

    if (transparent_value) {
      header_element.classList.add(this.klass.transparent);
      this.transparentEnable();
    } else {
      header_element.classList.remove(this.klass.transparent);
      this.transparentDisable();
    }

    if (instant) {
      header_element.offsetHeight; // force reflow
      header_element.classList.remove(this.klass.instant_bg_change);
    }

    this.onTransparent(instant);
  }

  // render 이후에 실행할 것
  public redrawBgToWhiteWhenScroll() {
    this.state.transparent = !(window.scrollY > 10);
    this.redrawBgColor();

    window.addEventListener(
      'scroll',
      throttleAnimationFrameLevel(() => {
        const transparent = !(window.scrollY > 10);
        if (this.state.transparent == transparent) return;
        this.state.transparent = transparent;

        this.redrawBgColor();
      }) as any,
    );
  }

  public getIsMobile(): boolean {
    const is_mobile = getSharedData(this)?.is_mobile ?? this.state.is_mobile;
    if (isNil(is_mobile)) throw new Error('is_mobile is not set');
    return is_mobile;
  }

  public setForceUnpinned() {
    const header_el = $(this.element()).find(`.${this.klass.header}`);
    return setForceUnpinned(header_el);
  }
}
